import { ContactType, IContact } from "./Contact";
import { IDocument } from "./Document";
import { IInvoice } from "./Invoice";
import { IJob } from "./Job";
import { IVehicle } from "./Vehicle";
import { BaseEntity, DefaultResponseWithData } from "./system";

export interface ILeadResponse {
  lastExported: string | null;
  lastImported: string | null;
  leads: ILead[];
}

export interface INewLead {
  CCID: string | null;
  website: LeadSource | null;
  source: string | null;
  price: number | null;
  commission: number | null;
  vehicleGrade: string | null;
  vehicle: {
    vrm: string | null;
    make: string | null;
    model: string | null;
    postcode: string | null;
    line1: string | null;
    line2: string | null;
    line3: string | null;
    line4: string | null;
    locality: string | null;
    town: string | null;
    county: string | null;
    isDropOff: boolean;
  };
  contact: {
    contactId: string;
    fullname: string | null;
    email: string | null;
    telephone1: string | null;
    telephone2: string | null;
  };
}

export interface ISingleLeadRespose extends DefaultResponseWithData<ILead> { }

export interface IAllLeadsResponse
  extends DefaultResponseWithData<ILeadResponse> { }

export interface IPaymentsLeadsResponse
  extends DefaultResponseWithData<ILead[]> { }

export interface IUnscheduledLeadsResponse
  extends DefaultResponseWithData<ILead[]> { }

export enum LeadStatus {
  "Awaiting Contact",
  "Scheduled",
  "Cancelled",
  "Reschedule",
  "Collected",
  "Awaiting Payment",
  "Awaiting COD",
  "Customer to Call Back",
  "Completed",
  "All",
  "Call List",
  "Allocated",
}

export enum LeadSource {
  Manual,
  SCC,
  CTB,
  RMC,
  Motorwise,
  All,
  SellAVehicle,
  Widget,
  RemoveMyCar,
  CarCo
}

export enum LeadDocumentFilter {
  "Past Hour",
  "Today",
  "This Week",
  "Past 7 Days",
  "This Month",
  "Past 30 Days",
  "All",
}

export enum LeadPaymentMethod {
  "Bank Transfer",
  "Cheque",
  "Cash"
}

export enum PaymentStatus {
  "Not Started",
  "Raised",
  "Sent",
  "Failed",
  "Approved",
}

export enum LeadQualified {
  "No Reply",
  "Qualified",
  "Stop",
  "No Match",
}

export interface ILead extends BaseEntity {
  tripId: string | null;
  truckId: string | null;

  contactId: string | null;

  driverId: string | null;
  driver: IContact | null;

  invoiceId: string | null;
  invoice: IInvoice | null;

  ccid: string | null;
  contact: IContact | null;
  officeNotes: string | null;
  driverNotes: string | null;
  yardNotes: string | null;
  vehicleId: string | null;
  vehicle: IVehicle | null;
  status: LeadStatus;
  callListStatus: CallListStatus | null;
  website: LeadSource;
  source: string | null;
  price: number;
  applyVAT: boolean;
  priceWithVAT: number;
  commission: number;
  notes: string;
  completedOn: string;
  qualified: LeadQualified;
  exportedOn: string | null;
  partsLookupRequestId: string | null;
  cancelledReason: string | null;
  cancelledBy: string | null;
  cancelledOn: string | null;
  paymentStatus: PaymentStatus;
  paymentMethod: LeadPaymentMethod;
  paymentMessage: string | null;
  paymentRaisedBy: string | null;
  paymentApprovedBy: string | null;
  paymentConfirmedOn: string;
  paymentType: string | null;
  accountName: string | null;
  accountNumber: string | null;
  sortCode: string | null;
  paymentReference: string | null;
  cardNumber: string | null;
  payeeDOB: string | null;
  chequeName: string | null;
  chequeNumber: string | null;
  premiumPriceUsed: string | null;
  scheme: string | null;
  vehicleGrade: string | null;
  callCount: number;
  callCountUpdatedOn: string | null;
  callCountUpdatedBy: string | null;
  documentsRequested: string | null;
  order: number;
  jobs: IJob[] | null;
  catalyticAveragePrice: number | null;
  correctedVrm: string | null;
  allocatedToCustomerId: string | null;

  deleted: string | null;
  deletedBy: string | null;

  // Widget
  quoteType: QuoteType;
  quoteTabType: QuoteTabType;
  quoteLocationId: string | null;

  roadSidePayment: boolean;
  fastPayment: boolean;
  collectionOptionId: string | null;
  collectionOptionName: string | null;
  collectionOptionPrice: number;
  accessToVehicle: boolean;
  v5c: boolean;
  isCollection: boolean;
  // json string of calculation data
  calculationJson: string | null;
  collectionCustomerId: string | null;
  dropOffCustomerId: string | null;

  tellerooTransactionId: string;

  documents: IDocument[] | null;
}

export enum CallListStatus
{
  CallListOne     = 0,
  EmailListOne    = 1,
  CallListTwo     = 2,
  EmailListTwo    = 3,
  CallListThree   = 4,
  EmailListThree  = 5,
  ManagerApproval = 6,
  Archived        = 7
}

export enum QuoteType {
  ScrapExport,
  Salvage,
  SmartPrice
}

// Read-only historical snapshot of the address quoted when the lead came in.
export interface IQuoteLocation extends BaseEntity {
  fullname: string;
  telephone1: string;
  telephone2: string;
  email: string;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  locality: string | null;
  town: string | null;
  county: string | null;
  postcode: string | null;
  longitude: number;
  latitude: number;
}

export interface ISingleQuoteLocationResponse extends DefaultResponseWithData<IQuoteLocation> { }

export interface ICalculationData {
  salvageCalculations: ICalculation[];
  tradePoorCalculations: ICalculation[];
  salvageVoucherCalculation: ICalculation;
  // scrap
  postcodeCalculations: ICalculation[];
  catalyticCalculations: ICalculation[];
  engineCalculations: ICalculation[];
  vfeCalculations: ICalculation[];
  generalCalculations: ICalculation[];
  genericCalculations: ICalculation[];
  additionalCalculations: ICalculation[];
  scrapVoucherCalculation: ICalculation;
  // lookup
  partsLookupCalculations: ICalculation[];
  partsLookupGenericCalculations: ICalculation[];
  // other
  dropOff: number;
  payment: ICalculation;
  collectionOption: ICalculation;
}

export interface ICalculation {
  name: string;
  isFixed: boolean;
  value: number;
  baseValue: number;
  isAddition: boolean;
  customCalculation: string;
}


export interface ILeadStats {
  awaitingContact: number;
  customerToCallBack: number;
  scheduled: number;
  reschedule: number;
  collected: number;
  collectedToday: number;
  awaitingCod: number;
  awaitingPayment: number;
  cancelled: number;
  completed: number;
  completedToday: number;
}


export interface ILeadActionsResponse extends DefaultResponseWithData<ILeadAction[]> { }
export interface ILeadActionResponse extends DefaultResponseWithData<ILeadAction> { }

export interface ILeadAction {
  id: string;
  leadId: string;
  actionType: LeadActionType;
  description: string;
  actionedBy?: string | null;
  created: string;
}

export enum LeadActionType
{
  Generic,
  Deleted,
  Cancelled,
  PaymentStatusChanged,
  StatusChanged,
  CallListStatusChanged,
  PriceChanged,
}

export enum QuoteTabType {
  Quotes,
  Atf,
  HighValue,
  HighPriority,
  ManagerAssistance
}
